import { morph } from '@theme/morph';

/**
 * Cart drawer behaviour.
 *
 * Extracted from snippets/cart-drawer.liquid on 2026-08-12. The drawer renders in
 * the header on EVERY page, so this was 10,524 bytes of uncached JS in every
 * document. Only two values needed Liquid; they now arrive as data attributes on
 * the <dialog>, which is what let the rest move out.
 *
 * Three functions stay on `window` because five other files call them:
 * snippets/cart-toast.liquid, snippets/cart-quantity-guard.liquid,
 * sections/main-product.liquid, sections/custom-header.liquid and
 * assets/custom-product-card.js. All of those call at click time, well after this
 * module has executed, so there is no load-order race.
 */

const DRAWER_ID = 'CartDrawer';

/** @returns {HTMLDialogElement | null} */
const getDrawer = () => /** @type {HTMLDialogElement | null} */ (document.getElementById(DRAWER_ID));

/** Config the server put on the dialog, replacing the two Liquid values. */
function config() {
  const drawer = getDrawer();
  return {
    currency: drawer?.dataset.currency || 'DKK',
    cartUpdateUrl: drawer?.dataset.cartUpdateUrl || '/cart/update',
  };
}

/**
 * Announce a cart change to screen readers.
 *
 * The live region lives inside the dialog on purpose: showModal() makes the rest
 * of the document inert, so a region outside it would never be read out.
 */
function announce(message) {
  const region = getDrawer()?.querySelector('[data-cd-status]');
  if (region) region.textContent = message;
}

/* ── Open ─────────────────────────────────────────────────────────────────── */

window.openCartDrawer = function (event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const drawer = getDrawer();
  if (drawer && !drawer.open) drawer.showModal();

  /* GA4: view_cart — user-initiated open only, with fresh cart data. */
  if (window.gtmPush && window.gtmFetchCartEcommerce) {
    window.gtmFetchCartEcommerce().then((ecommerce) => {
      window.gtmPush({ event: 'view_cart', ecommerce });
    });
  }
};

/* Close on backdrop click. Bound to the dialog itself, which morph never
   replaces, so this survives every refresh. */
const drawerEl = getDrawer();
if (drawerEl) {
  drawerEl.addEventListener('click', (event) => {
    if (event.target === drawerEl) drawerEl.close();
  });
}

/* Auto-open after an add-to-cart that fell back to a full page reload. */
try {
  if (sessionStorage.getItem('openCartDrawer') === '1') {
    sessionStorage.removeItem('openCartDrawer');
    getDrawer()?.showModal();
  }
} catch (error) {
  /* Private mode — the drawer simply does not auto-open. */
}

/* ── Refresh ──────────────────────────────────────────────────────────────── */

/**
 * Re-render the drawer from the Section Rendering API.
 *
 * This used to do `drawer.innerHTML = fresh.innerHTML`, which threw away the
 * whole subtree on every +/- click. That reset the scroll position, closed any
 * open accordion, and — because the note textarea was replaced while its input
 * listener stayed bound to the old element — permanently killed cart-note
 * auto-saving after the first quantity change.
 *
 * morph() patches the existing nodes in place instead, so element identity (and
 * therefore scroll position, focus, and <details> open state) survives. It also
 * handles textarea and input values specifically, so a half-typed note is kept.
 */
window.refreshCartDrawer = function (options) {
  const shouldOpen = Boolean(options && options.open);
  const sectionId =
    document.querySelector('#' + DRAWER_ID)?.closest('.shopify-section')?.id?.replace('shopify-section-', '') ||
    'custom-header';

  const failOver = () => {
    try {
      sessionStorage.setItem('openCartDrawer', shouldOpen ? '1' : '0');
    } catch (error) {
      /* ignore */
    }
    window.location.reload();
  };

  fetch('/?sections=' + sectionId + ',custom-header')
    .then((response) => response.json())
    .then((sections) => {
      const html = sections[sectionId] || sections['custom-header'];
      if (!html) return failOver();

      /* DOMParser rather than innerHTML on a detached div — the pattern
         assets/section-renderer.js already uses for section responses. */
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('#' + DRAWER_ID);
      const current = getDrawer();
      if (!fresh || !current) return;

      const wasOpen = current.open;
      const scroller = current.querySelector('.cd__items');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      morph(current, fresh);

      /* morph preserves the node, but the list may have shrunk under the
         previous offset — clamp rather than assume. */
      const scrollerAfter = current.querySelector('.cd__items');
      if (scrollerAfter) {
        scrollerAfter.scrollTop = Math.min(scrollTop, scrollerAfter.scrollHeight);
      }

      const count = parsed.querySelector('.ch__cart-bubble')?.textContent.trim();
      if (count) {
        for (const bubble of document.querySelectorAll('.ch__cart-bubble')) {
          bubble.textContent = count;
        }
      }

      const subtotal = current.querySelector('.cd__subtotal-value')?.textContent.trim();
      announce([count ? count + ' ' : '', subtotal || ''].join('').trim());

      /* the recs block is re-rendered by the morph above, so re-run the upgrade */
      enhanceRecommendations();

      if (shouldOpen || wasOpen) current.showModal();
    })
    .catch(failOver);
};

/* ── Notices ──────────────────────────────────────────────────────────────────
   Shopify's own message, shown where the shopper is looking. The drawer had no
   error element at all, so a rejected quantity change was completely silent.
   Survives the morph because it is re-created on each failure. */

function clearNotice() {
  lastNotice = '';
  getDrawer()?.querySelector('[data-cd-notice]')?.remove();
}

let lastNotice = '';

function showNotice(message) {
  const drawer = getDrawer();
  if (!drawer || !message) return;
  /* clear first: clearNotice() also resets lastNotice, so setting it before
     this call would immediately wipe the value we are about to need. */
  clearNotice();
  lastNotice = message;

  const el = document.createElement('p');
  el.className = 'cd__notice';
  el.setAttribute('data-cd-notice', '');
  el.setAttribute('role', 'status');
  el.textContent = message;

  const items = drawer.querySelector('.cd__items');
  if (items) items.prepend(el);
  announce(message);
}

/* ── Quantity ─────────────────────────────────────────────────────────────── */

const quantityTimers = {};

window.updateCartDrawerQty = function (key, quantity) {
  if (quantityTimers[key]) clearTimeout(quantityTimers[key]);

  const row = document.querySelector('.cd__item[data-key="' + key + '"]');

  /* Optimistic UI so the click feels instant; the refresh below is the truth. */
  if (quantity === 0 && row) {
    row.classList.add('is-removing');
    if (window.gtmPush) {
      window.gtmPush({
        event: 'remove_from_cart',
        ecommerce: {
          currency: config().currency,
          value: (parseFloat(row.dataset.itemPrice) || 0) * (parseInt(row.dataset.itemQuantity, 10) || 1),
          items: [
            {
              item_id: row.dataset.itemId,
              item_name: row.dataset.itemName,
              item_variant: row.dataset.itemVariant,
              price: parseFloat(row.dataset.itemPrice) || 0,
              quantity: parseInt(row.dataset.itemQuantity, 10) || 1,
            },
          ],
        },
      });
    }
  } else if (row) {
    row.classList.add('is-updating');
    const count = row.querySelector('.cd__qty-count');
    if (count && quantity > 0) count.textContent = String(quantity);
  }

  quantityTimers[key] = setTimeout(() => {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then(async (response) => {
        const payload = await response.json();

        /* A 422 still parses as JSON, so `response.ok` has to be checked
           explicitly. Without it the error body was treated as a cart, and
           `cart.item_count` — undefined — was written into the bubble.

           This is what made "+" on a last-in-stock item look broken: the
           number flicked up, silently reverted, and Shopify's perfectly good
           explanation ("Der blev kun føjet 1 vare til din indkøbskurv, fordi
           der ikke er flere tilgængelige") was discarded. Shopify already
           localises that string, so it is shown as-is rather than replaced
           with a guess of our own. */
        if (!response.ok) {
          showNotice(payload.description || payload.message || '');
          throw new Error('cart change rejected');
        }
        return payload;
      })
      .then((cart) => {
        clearNotice();
        for (const bubble of document.querySelectorAll('.ch__cart-bubble')) {
          bubble.textContent = cart.item_count;
        }
        window.refreshCartDrawer({ open: true });
      })
      .catch(() => {
        row?.classList.remove('is-updating', 'is-removing');
        /* The refresh morphs .cd__items, which throws away a notice prepended
           to it. So re-apply after the refresh settles rather than before —
           the first version of this fix showed the message and then deleted
           it a few hundred milliseconds later. */
        const pending = lastNotice;
        window.refreshCartDrawer({ open: true });
        if (pending) setTimeout(() => showNotice(pending), 900);
      });
  }, 150);
};

/* ── Delegated UI handlers ────────────────────────────────────────────────────
   These replace nine inline onclick attributes. Delegation is not a style
   preference here: a handler bound to a specific node dies the moment that node
   is re-rendered, which is exactly how the cart-note listener was lost. */

document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);

  const closer = target.closest?.('[data-cd-close]');
  if (closer) {
    /* Links keep navigating; the drawer just gets out of the way. */
    getDrawer()?.close();
    return;
  }

  const stepper = target.closest?.('[data-cd-qty]');
  if (stepper) {
    event.preventDefault();
    window.updateCartDrawerQty(
      stepper.getAttribute('data-cd-qty'),
      parseInt(stepper.getAttribute('data-cd-qty-to') || '0', 10)
    );
  }
});

/* ── Cart note ────────────────────────────────────────────────────────────────
   Delegated so it cannot be orphaned by a re-render again. morph now preserves
   the textarea too, but the listener must not depend on that. */

let noteTimer;
document.addEventListener('input', (event) => {
  const note = /** @type {HTMLTextAreaElement} */ (event.target);
  if (!note.matches?.('[data-cart-note]')) return;
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.value }),
    });
  }, 500);
});

/* ── Discount code ────────────────────────────────────────────────────────── */

function appliedDiscountCodes() {
  return Array.from(document.querySelectorAll('#' + DRAWER_ID + ' .cd__discount-pill'))
    .map((pill) => pill.getAttribute('data-discount-code'))
    .filter(Boolean);
}

function submitDiscounts(codes, applyButton) {
  if (applyButton) applyButton.disabled = true;
  return fetch(config().cartUpdateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') }),
  })
    .then((response) => response.json())
    .finally(() => {
      if (applyButton) applyButton.disabled = false;
    });
}

document.addEventListener('submit', (event) => {
  const form = /** @type {Element} */ (event.target).closest?.('[data-cart-discount-form]');
  if (!form) return;
  event.preventDefault();

  const input = form.querySelector('input[name="discount"]');
  const error = document.querySelector('#' + DRAWER_ID + ' [data-cart-discount-error]');
  const applyButton = form.querySelector('.cd__discount-apply');
  if (!input || !input.value.trim()) return;

  const code = input.value.trim();
  const existing = appliedDiscountCodes();
  if (existing.includes(code)) {
    input.value = '';
    return;
  }
  error?.classList.add('hidden');

  submitDiscounts(existing.concat([code]), applyButton).then((data) => {
    const rejected = (data.discount_codes || []).some((d) => d.code === code && d.applicable === false);
    if (rejected) {
      error?.classList.remove('hidden');
      input.value = '';
      return;
    }
    window.refreshCartDrawer({ open: true });
  });
});

document.addEventListener('click', (event) => {
  const remove = /** @type {Element} */ (event.target).closest?.('[data-cart-discount-remove]');
  if (!remove) return;
  event.preventDefault();
  const code = remove.getAttribute('data-cart-discount-remove');
  const remaining = appliedDiscountCodes().filter((c) => c !== code);
  submitDiscounts(remaining, null).then(() => window.refreshCartDrawer({ open: true }));
});

/* ── Checkout ─────────────────────────────────────────────────────────────────
   The re-submit MUST pass the original submitter. Without it the button's
   name="checkout" never reaches Shopify and the cart loops back to /cart — the
   bug fixed in f7b95f6. Do not simplify this to form.submit(). */

document.addEventListener('submit', (event) => {
  const form = /** @type {Element} */ (event.target).closest?.('.cd__checkout-form');
  if (!form) return;
  if (!window.gtmPush || !window.gtmFetchCartEcommerce) return;
  if (form.dataset.gtmProceed === '1') return;

  event.preventDefault();
  const submitter = event.submitter;

  const proceed = () => {
    form.dataset.gtmProceed = '1';
    if (form.requestSubmit) {
      form.requestSubmit(submitter || undefined);
    } else if (submitter) {
      submitter.click();
    } else {
      form.submit();
    }
  };

  /* Never let analytics stand between a shopper and checkout. */
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    proceed();
  };
  setTimeout(go, 600);
  window.gtmFetchCartEcommerce()
    .then((ecommerce) => {
      window.gtmPush({ event: 'begin_checkout', ecommerce });
      go();
    })
    .catch(go);
});

/* ── Safety cross-sell: one-tap add ───────────────────────────────────────── */

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-cd-quick-add]') : null;
  if (!button) return;

  event.preventDefault();
  const id = button.getAttribute('data-cd-quick-add');
  if (!id || button.disabled) return;

  button.disabled = true;
  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
  })
    .then((response) => {
      if (!response.ok) throw new Error('add failed');
      return window.refreshCartDrawer({ open: true });
    })
    .catch(() => {
      /* leave the row in place so the shopper can try again */
      button.disabled = false;
    });
});

/* ── Recommendations: upgrade the over-threshold list ─────────────────────────
   Gap mode is left alone — those picks are chosen to close the free-shipping
   shortfall, which the recommendations engine cannot do. Once the shopper is
   over the threshold there is no gap to close, so complementary products are a
   better answer than "popular". Order is complementary → related → whatever the
   server already rendered, and a result set smaller than three is treated as no
   result: a single stray suggestion looks worse than the collection row. */

const RECS_WANTED = 3;

async function fetchRecommendations(productId, intent) {
  const url =
    window.Shopify.routes.root +
    'recommendations/products.json?product_id=' +
    encodeURIComponent(productId) +
    '&limit=4&intent=' +
    intent;

  const response = await fetch(url);
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

function renderRecommendation(product) {
  const item = document.createElement('li');
  item.className = 'cfr__item';

  const link = document.createElement('a');
  link.className = 'cfr__link';
  link.href = product.url;
  link.setAttribute('data-cd-close', '');

  if (product.featured_image) {
    const image = document.createElement('img');
    image.className = 'cfr__img';
    image.src = product.featured_image;
    image.alt = product.title;
    image.loading = 'lazy';
    image.width = 44;
    image.height = 44;
    link.appendChild(image);
  }

  const text = document.createElement('span');
  text.className = 'cfr__text';

  const title = document.createElement('span');
  title.className = 'cfr__title';
  title.textContent = product.title;

  const price = document.createElement('span');
  price.className = 'cfr__price';
  /* price is in cents; the drawer already carries the shop's currency */
  price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'da', {
    style: 'currency',
    currency: getDrawer()?.getAttribute('data-currency') || 'DKK',
  }).format(product.price / 100);

  text.append(title, price);
  link.appendChild(text);
  item.appendChild(link);
  return item;
}

async function enhanceRecommendations() {
  const block = document.querySelector('[data-cd-recs]');
  if (!block || block.getAttribute('data-cd-recs-mode') === 'gap') return;
  if (block.hasAttribute('data-cd-recs-done')) return;

  const productId = block.getAttribute('data-cd-recs-product');
  const list = block.querySelector('.cd__recs-list');
  if (!productId || !list) return;

  try {
    let products = await fetchRecommendations(productId, 'complementary');
    if (products.length < RECS_WANTED) {
      products = await fetchRecommendations(productId, 'related');
    }
    if (products.length < RECS_WANTED) return; /* keep the server-rendered row */

    list.replaceChildren(...products.slice(0, 4).map(renderRecommendation));
    block.setAttribute('data-cd-recs-done', '');
  } catch {
    /* network or shape problem — the server-rendered row stays */
  }
}

enhanceRecommendations();
